import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Calculator, 
  LogOut, 
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  Save,
  Trophy,
  Percent,
  Euro
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPremiums = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [userGroups, setUserGroups] = useState([]);
  const [tiers, setTiers] = useState([
    { min_margin: 0, max_margin: 10, value_type: "percentage", premium_percentage: 0, fixed_value: 0, group_1_percentage: 0, group_2_percentage: 0, group_3_percentage: 0, description: "Sem prémio" },
    { min_margin: 10, max_margin: 15, value_type: "percentage", premium_percentage: 5, fixed_value: 0, group_1_percentage: 5, group_2_percentage: 4, group_3_percentage: 3, description: "Prémio Básico" },
    { min_margin: 15, max_margin: 20, value_type: "percentage", premium_percentage: 10, fixed_value: 0, group_1_percentage: 10, group_2_percentage: 8, group_3_percentage: 6, description: "Prémio Standard" },
    { min_margin: 20, max_margin: 25, value_type: "percentage", premium_percentage: 15, fixed_value: 0, group_1_percentage: 15, group_2_percentage: 12, group_3_percentage: 9, description: "Prémio Premium" },
    { min_margin: 25, max_margin: null, value_type: "percentage", premium_percentage: 20, fixed_value: 0, group_1_percentage: 20, group_2_percentage: 16, group_3_percentage: 12, description: "Prémio Excelência" }
  ]);
  const [groupNames, setGroupNames] = useState({
    group_1: "IT Recruiter",
    group_2: "Business Manager",
    group_3: "People",
  });
  // ---- Escalões por Função de Consultor ----
  // `settingsFull` guarda o doc completo (tiers globais + per_function).
  // `activeFunction`="" → editar Global; caso contrário edita per_function[name].
  const [settingsFull, setSettingsFull] = useState({ tiers: [], per_function: {} });
  const [jobFunctions, setJobFunctions] = useState([]);
  const [activeFunction, setActiveFunction] = useState("");

  const loadJobFunctions = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/job-functions`, { headers: getAuthHeaders() });
      setJobFunctions((r.data || []).filter(f => f?.name));
    } catch (e) {
      console.error("[AdminPremiums] loadJobFunctions:", e);
    }
  }, [getAuthHeaders]);

  const loadUserGroups = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/user-groups`, { headers: getAuthHeaders() });
      setUserGroups(r.data || []);
    } catch (e) {
      console.error("[AdminPremiums] loadUserGroups:", e);
      // keep free-text fallback
    }
  }, [getAuthHeaders]);

  const loadSettings = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/premium-settings`, {
        headers: getAuthHeaders()
      });
      const data = response.data || {};
      setSettingsFull({
        tiers: data.tiers || [],
        per_function: data.per_function || {},
      });
      if (data?.tiers?.length > 0) {
        setTiers(data.tiers);
      }
      if (data?.group_names) {
        setGroupNames(data.group_names);
      }
    } catch (error) {
      console.error("Error loading premium settings:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadSettings();
    loadUserGroups();
    loadJobFunctions();
  }, [loadSettings, loadUserGroups, loadJobFunctions]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const addTier = () => {
    const lastTier = tiers[tiers.length - 1];
    const newMinMargin = lastTier?.max_margin || (lastTier?.min_margin + 500) || 0;
    setTiers([...tiers, {
      min_margin: newMinMargin,
      max_margin: null,
      value_type: "percentage",
      premium_percentage: 0,
      fixed_value: 0,
      bounds_unit: "euro",
      description: "Novo escalão"
    }]);
  };

  const removeTier = (index) => {
    setTiers(tiers.filter((_, i) => i !== index));
  };

  const updateTier = (index, field, value) => {
    const newTiers = [...tiers];
    if (field === "max_margin" && value === "") {
      newTiers[index][field] = null;
    } else if (field === "description" || field === "value_type") {
      newTiers[index][field] = value;
    } else {
      newTiers[index][field] = parseFloat(value) || 0;
    }
    setTiers(newTiers);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Todos os escalões usam bounds em € (margem libertada) — o backend
      // compara min/max contra `margin_value` em vez de `margin_percentage`.
      const tiersToSave = tiers.map(t => ({ ...t, bounds_unit: "euro" }));
      // Persiste no slot certo: Global (settingsFull.tiers) OU per_function[name].
      const nextFull = {
        tiers: activeFunction === "" ? tiersToSave : (settingsFull.tiers || []),
        per_function: { ...(settingsFull.per_function || {}) },
      };
      if (activeFunction !== "") {
        nextFull.per_function[activeFunction] = { tiers: tiersToSave };
      }
      await axios.post(`${API}/premium-settings`, {
        tiers: nextFull.tiers,
        per_function: nextFull.per_function,
        group_names: groupNames,
      }, {
        headers: getAuthHeaders()
      });
      setSettingsFull(nextFull);
      toast.success(activeFunction === ""
        ? "Escalões globais guardados!"
        : `Escalões para "${activeFunction}" guardados!`);
    } catch (error) {
      toast.error("Erro ao guardar configurações");
    } finally {
      setSaving(false);
    }
  };

  // Muda o "slot" a editar. Ao mudar, carrega os tiers desse slot (herda
  // do global se a função ainda não tem escalões próprios).
  const switchActiveFunction = (name) => {
    setActiveFunction(name);
    if (name === "") {
      setTiers(settingsFull.tiers || []);
    } else {
      const fnTiers = (settingsFull.per_function || {})[name]?.tiers;
      setTiers(fnTiers && fnTiers.length ? fnTiers : (settingsFull.tiers || []));
    }
  };

  // Limpa os escalões da função actual → volta a herdar do Global.
  const resetActiveFunction = () => {
    if (activeFunction === "") return;
    if (!window.confirm(`Remover escalões específicos da função "${activeFunction}"? Vai passar a usar os escalões Globais.`)) return;
    const nextPer = { ...(settingsFull.per_function || {}) };
    delete nextPer[activeFunction];
    setSettingsFull({ ...settingsFull, per_function: nextPer });
    setTiers(settingsFull.tiers || []);
    axios.post(`${API}/premium-settings`, {
      tiers: settingsFull.tiers || [],
      per_function: nextPer,
      group_names: groupNames,
    }, { headers: getAuthHeaders() })
      .then(() => toast.success("Escalões da função removidos — passa a usar os Globais."))
      .catch(() => toast.error("Erro ao remover"));
  };

  const getTierColor = (percentage) => {
    if (percentage === 0) return "bg-gray-100 border-gray-300";
    if (percentage <= 5) return "bg-blue-50 border-blue-300";
    if (percentage <= 10) return "bg-brand-50 border-brand-300";
    if (percentage <= 15) return "bg-yellow-50 border-yellow-300";
    return "bg-amber-50 border-amber-300";
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-premiums-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">Margem de Negócio</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Back & Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl text-foreground flex items-center gap-2">
                <Trophy className="w-6 h-6 text-amber-500" />
                Margem de Negócio
              </h1>
              <p className="text-muted-foreground">
                Configure os prémios por margem em 3 grupos
              </p>
            </div>
          </div>
          
          <Button 
            onClick={handleSave}
            disabled={saving}
            className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
            data-testid="save-premiums-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Guardar
          </Button>
        </div>

        {/* Info Box */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-blue-800">
              <strong>Como funciona:</strong> Cada escalão define os limites de <strong>margem libertada (€)</strong> — mín. e máx. em euros — e o prémio a atribuir (valor fixo €
              ou % sobre a margem libertada). O escalão aplicável é escolhido pela margem libertada da simulação em €.
            </p>
          </CardContent>
        </Card>

        {/* Selector de Função — escolhe o "slot" a editar */}
        <Card className="mb-6 bg-white border border-border/60 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3 flex-wrap">
            <Label className="text-sm font-semibold m-0">A configurar escalões para:</Label>
            <Select value={activeFunction || "__global__"} onValueChange={(v) => switchActiveFunction(v === "__global__" ? "" : v)}>
              <SelectTrigger className="w-64" data-testid="premium-function-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__global__">Global (fallback)</SelectItem>
                {jobFunctions.map((f) => {
                  const hasOverride = !!(settingsFull.per_function || {})[f.name];
                  return (
                    <SelectItem key={f.id || f.name} value={f.name}>
                      {f.name} {hasOverride ? "✓" : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            <span className="text-xs text-muted-foreground flex-1">
              {activeFunction === ""
                ? "Estes escalões aplicam-se quando o consultor não tem escalões específicos para a sua Função."
                : `Estes escalões só são usados quando o consultor tem Função "${activeFunction}". Fallback: escalões Globais.`}
            </span>
            {activeFunction !== "" && (
              <Button variant="outline" size="sm" onClick={resetActiveFunction} data-testid="premium-reset-function-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Voltar ao Global
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Tiers Configuration */}
        <Card className="bg-white border border-border/60 shadow-sm">
          <CardHeader className="border-b">
            <CardTitle className="font-manrope text-lg flex items-center justify-between">
              <span>Escalões de Prémio</span>
              <Button variant="outline" size="sm" onClick={addTier}>
                <Plus className="w-4 h-4 mr-1" />
                Adicionar
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-4">
                {tiers.map((tier, index) => {
                  return (
                  <div 
                    key={index} 
                    className={`p-4 rounded-lg border-2 ${getTierColor(tier.value_type === "fixed" ? tier.fixed_value : tier.premium_percentage)}`}
                  >
                    <div className="grid grid-cols-6 gap-3 items-end">
                      <div>
                        <Label className="text-xs text-muted-foreground">Margem Mín. (€)</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            min="0"
                            step="50"
                            className="font-mono pr-8"
                            value={tier.min_margin}
                            onChange={(e) => updateTier(index, "min_margin", e.target.value)}
                            data-testid={`tier-min-margin-${index}`}
                          />
                          <Euro className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-xs text-muted-foreground">Margem Máx. (€)</Label>
                        <div className="relative">
                          <Input
                            type="number"
                            min="0"
                            step="50"
                            className="font-mono pr-8"
                            value={tier.max_margin === null ? "" : tier.max_margin}
                            onChange={(e) => updateTier(index, "max_margin", e.target.value)}
                            placeholder="∞"
                            data-testid={`tier-max-margin-${index}`}
                          />
                          <Euro className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-xs text-muted-foreground">Tipo Prémio</Label>
                        <Select
                          value={tier.value_type || "percentage"}
                          onValueChange={(value) => updateTier(index, "value_type", value)}
                        >
                          <SelectTrigger className="h-10">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">% Margem</SelectItem>
                            <SelectItem value="fixed">Valor Fixo €</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {(tier.value_type || "percentage") === "percentage" ? (
                        <div>
                          <Label className="text-xs text-muted-foreground">Prémio (%)</Label>
                          <div className="relative">
                            <Input
                              type="number"
                              min="0"
                              step="1"
                              className="font-mono pr-8 bg-white"
                              value={tier.premium_percentage || 0}
                              onChange={(e) => updateTier(index, "premium_percentage", e.target.value)}
                            />
                            <Percent className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      ) : (
                        <div>
                          <Label className="text-xs text-muted-foreground">Valor (€)</Label>
                          <div className="relative">
                            <Input
                              type="number"
                              min="0"
                              step="10"
                              className="font-mono pl-8 bg-white"
                              value={tier.fixed_value || 0}
                              onChange={(e) => updateTier(index, "fixed_value", e.target.value)}
                            />
                            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          </div>
                        </div>
                      )}
                      
                      <div>
                        <Label className="text-xs text-muted-foreground">Descrição</Label>
                        <Input
                          type="text"
                          value={tier.description}
                          onChange={(e) => updateTier(index, "description", e.target.value)}
                          placeholder="Nome do escalão"
                        />
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removeTier(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {/* 3 grupos removidos — cada escalão tem 1 prémio único (tipo + valor) */}
                    
                    <p className="mt-2 text-xs text-muted-foreground">
                      Margem libertada €{tier.min_margin}-{tier.max_margin === null || tier.max_margin === "" ? "∞" : `€${tier.max_margin}`}:
                      {(tier.value_type || "percentage") === "percentage" 
                        ? ` Prémio de ${tier.premium_percentage || 0}% sobre a margem libertada`
                        : ` Prémio fixo de €${tier.fixed_value || 0}`}
                    </p>
                  </div>
                );})}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminPremiums;
